import React, { useState, useRef } from 'react';
import { Button } from '@common/Button';
import { Upload, FileText, Check, AlertTriangle, Info } from 'lucide-react';
import { importHauteursLibres, importSampleData } from '@utils/wfs/importHauteursLibres';
import { logError, logInfo } from '@utils/logger';
import Papa from 'papaparse';
import { Toast } from '@common/Toast';

export const HauteursLibresImport = () => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.type !== 'text/csv' && !selectedFile.name.endsWith('.csv')) {
        setError('Please select a CSV file');
        setFile(null);
        return;
      }
      setFile(selectedFile);
      setError(null);
    }
  };

  const handleImport = async () => {
    if (!file) {
      setError('Please select a CSV file first');
      return;
    }

    setLoading(true);
    setError(null);
    setResults(null);

    try {
      logInfo(`Starting import of CSV file: ${file.name}`);
      const importResults = await importHauteursLibres(file);
      
      setResults(importResults);
      setToast({
        type: 'success',
        message: `Import completed: ${importResults.success.length} structures imported, ${importResults.failed.length} failed`
      });
      
      logInfo('CSV import completed successfully');
    } catch (error) {
      logError('Failed to import CSV file:', error);
      setError(error.message || 'Failed to import CSV file');
      setToast({
        type: 'error',
        message: error.message || 'Failed to import CSV file'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImportSampleData = async () => {
    setLoading(true);
    try {
      const results = await importSampleData();
      setResults(results);
      logInfo('Sample data imported successfully');
    } catch (error) {
      logError('Failed to import sample data:', error);
      setError('Failed to import sample data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        {/* File Upload Section */}
        <div className="bg-gray-700/50 p-4 rounded-lg">
          <div className="flex items-start gap-2 mb-4">
            <Info className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
            <div className="text-sm text-gray-300">
              <p className="mb-1">
                Please ensure your CSV file contains the following columns:
              </p>
              <ul className="list-disc list-inside">
                <li>reference</li>
                <li>route</li>
                <li>sequence</li>
                <li>municipalite</li>
                <li>obstacle</li>
                <li>direction_SO</li>
                <li>hauteur_SO</li>
                <li>direction_NE</li>
                <li>hauteur_NE</li>
                <li>latitude</li>
                <li>longitude</li>
              </ul>
            </div>
          </div>

          <div className="border-2 border-dashed border-gray-600 rounded-lg p-6">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept=".csv"
              className="hidden"
            />
            
            {file ? (
              <div className="flex items-center gap-3">
                <FileText className="w-6 h-6 text-blue-400" />
                <span className="text-white">{file.name}</span>
                <button
                  onClick={() => {
                    setFile(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                  className="text-gray-400 hover:text-white"
                >
                  Remove
                </button>
              </div>
            ) : (
              <div className="text-center">
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-300 mb-2">
                  Drag and drop your CSV file here, or
                </p>
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  variant="secondary"
                >
                  Browse Files
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Results Section */}
        <div className="bg-gray-700/50 p-4 rounded-lg overflow-hidden">
          <h3 className="text-white font-medium mb-4">Import Results</h3>
          
          {loading ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto"></div>
              <p className="text-gray-300 mt-2">Importing data...</p>
            </div>
          ) : results ? (
            <div className="space-y-4">
              {/* Success Results */}
              <div className="bg-green-500/10 p-3 rounded-lg overflow-x-auto">
                <h4 className="text-green-400 font-medium mb-2">
                  Successful Imports ({results.success.length})
                </h4>
                {results.success.length > 0 ? (
                  <table className="w-full text-sm table-fixed">
                    <thead>
                      <tr>
                        <th className="text-left py-1 px-2 text-green-300 w-1/3">Reference</th>
                        <th className="text-left py-1 px-2 text-green-300 w-2/3">Obstacle</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.success.map((item, index) => (
                        <tr key={index} className={index % 2 === 0 ? 'bg-green-500/5' : ''}>
                          <td className="py-1 px-2 text-green-200 whitespace-nowrap">{item.reference}</td>
                          <td className="py-1 px-2 text-green-200 truncate">{item.obstacle || 'N/A'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-gray-400">No successful imports</p>
                )}
              </div>
              
              {/* Failed Results */}
              {results.failed.length > 0 && (
                <div className="bg-red-500/10 p-3 rounded-lg overflow-x-auto">
                  <h4 className="text-red-400 font-medium mb-2">
                    Failed Imports ({results.failed.length})
                  </h4>
                  <table className="w-full text-sm table-fixed">
                    <thead>
                      <tr>
                        <th className="text-left py-1 px-2 text-red-300 w-1/3">Reference</th>
                        <th className="text-left py-1 px-2 text-red-300 w-2/3">Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.failed.map((item, index) => (
                        <tr key={index} className={index % 2 === 0 ? 'bg-red-500/5' : ''}>
                          <td className="py-1 px-2 text-red-200 whitespace-nowrap">{item.reference}</td>
                          <td className="py-1 px-2 text-red-200 truncate">{item.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : error ? (
            <div className="bg-red-500/10 p-3 rounded-lg">
              <div className="flex items-center gap-2 text-red-400">
                <AlertTriangle className="w-5 h-5" />
                <p>{error}</p>
              </div>
            </div>
          ) : null}
        </div>

        <Button
          onClick={handleImport}
          disabled={!file || loading}
          className="w-full"
        >
          {loading ? 'Importing...' : 'Import CSV'}
        </Button>
        <Button
          onClick={handleImportSampleData}
          className="bg-blue-600 hover:bg-blue-700 text-white"
          disabled={loading}
        >
          Import Sample Data
        </Button>
      </div>

      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default HauteursLibresImport;